import React from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Chip, 
  Avatar,
  IconButton
} from '@mui/material';
import { 
  Group as GroupIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme, backgroundImage }) => ({
  height: 200,
  position: 'relative',
  cursor: 'pointer',
  borderRadius: 16,
  overflow: 'hidden',
  background: backgroundImage ? `url(${backgroundImage})` : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
  transition: 'all 0.3s ease-in-out',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.1) 100%)',
    zIndex: 1,
  }
}));

const ContentOverlay = styled(CardContent)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  color: 'white',
  padding: theme.spacing(2),
  '&:last-child': {
    paddingBottom: theme.spacing(2)
  }
}));

const CountdownChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  color: theme.palette.text.primary,
  fontWeight: 'bold',
  fontSize: '0.75rem'
}));

const ParticipantChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  color: 'white',
  fontWeight: 'bold',
  fontSize: '0.75rem',
  '& .MuiChip-icon': {
    color: 'white'
  }
}));

const CreatorInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginTop: 'auto'
}));

const challengeBackgrounds = [
  'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)', // Green to dark green
  'linear-gradient(135deg, #81C784 0%, #388E3C 100%)', // Light green to forest green
  'linear-gradient(135deg, #66BB6A 0%, #1B5E20 100%)', // Medium green to very dark green
  'linear-gradient(135deg, #A5D6A7 0%, #2E7D32 100%)', // Very light green to dark green
  'linear-gradient(135deg, #00C853 0%, #1B5E20 100%)', // Bright green to very dark green
  'linear-gradient(135deg, #69F0AE 0%, #00C853 100%)', // Light mint to bright green
  'linear-gradient(135deg, #26A69A 0%, #004D40 100%)', // Teal to dark teal
  'linear-gradient(135deg, #80CBC4 0%, #00695C 100%)'  // Light teal to medium teal
];

function ChallengeCard({ 
  challenge,
  onClick,
  backgroundIndex = 0 
}) {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const calculateCountdown = (startDate, endDate) => {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (now < start) {
      const daysUntilStart = Math.ceil((start - now) / (1000 * 60 * 60 * 24));
      return {
        text: `STARTS IN ${daysUntilStart} DAY${daysUntilStart !== 1 ? 'S' : ''}`,
        color: 'info'
      };
    } else if (now <= end) {
      const daysLeft = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
      return {
        text: `${daysLeft} DAY${daysLeft !== 1 ? 'S' : ''} LEFT`,
        color: 'warning'
      };
    } else {
      return {
        text: 'ENDED',
        color: 'default'
      };
    }
  };

  const countdown = calculateCountdown(challenge.start_date, challenge.end_date);
  const backgroundGradient = challengeBackgrounds[backgroundIndex % challengeBackgrounds.length];

  return (
    <StyledCard 
      onClick={() => onClick?.(challenge)}
      style={{ background: backgroundGradient }}
    >
      <ContentOverlay>
        {/* Header with title and countdown */}
        <Box>
          <Typography 
            variant="h6" 
            component="h3" 
            gutterBottom
            sx={{ 
              fontWeight: 'bold',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              lineHeight: 1.2
            }}
          >
            {challenge.title || challenge.name}
          </Typography>
          
          <Typography 
            variant="body2" 
            sx={{ 
              opacity: 0.9,
              textShadow: '0 1px 2px rgba(0,0,0,0.3)',
              marginBottom: 1
            }}
          >
            {formatDate(challenge.start_date)} - {formatDate(challenge.end_date)}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <CountdownChip
              size="small"
              label={countdown.text}
              icon={<TimeIcon />}
            />
            <ParticipantChip
              size="small"
              label={`${challenge.participants || 0} participants`}
              icon={<GroupIcon />}
            />
          </Box>
        </Box>

        {/* Creator info at bottom */}
        <CreatorInfo>
          <Avatar 
            sx={{ 
              width: 24, 
              height: 24, 
              bgcolor: 'rgba(255, 255, 255, 0.3)' 
            }}
          >
            <PersonIcon fontSize="small" />
          </Avatar>
          <Typography 
            variant="caption" 
            sx={{ 
              opacity: 0.9,
              textShadow: '0 1px 2px rgba(0,0,0,0.3)'
            }}
          >
            Created by: {challenge.creator?.display_name || challenge.creator?.name || 'Unknown'}
          </Typography>
        </CreatorInfo>
      </ContentOverlay>
    </StyledCard>
  );
}

export default ChallengeCard;